import { useState, useEffect, useMemo } from "react"
import PixelReveal from "./PixelReveal"
import PixelIcon from "./PixelIcon"

// Quantitative "metrics" inside a description: percentages, counts, and
// comparisons like 92%, 270,000+, ~82%, <128, sub-15, >70%. The look-behind /
// look-ahead keep us from lighting up digits that live inside product names
// (ESP32, GPT-4o, Qwen3-VL-8B), which never carry a metric suffix.
const METRIC_SRC = "(?<![A-Za-z0-9])(?:sub[- ])?[~<>$]?\\d+(?:[.,]\\d+)*(?:%|\\+|\\$)?(?![A-Za-z])"

function escapeRegExp(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

// Build one regex that matches either a metric token or any of this row's
// languages / tooling, so both glow in the brand color wherever they appear —
// the stack line *and* inline in the prose. Terms are sorted longest-first so
// e.g. "AWS CDK" wins over a bare "AWS", and the alnum boundaries stop a term
// from matching inside a larger word (so "SQL" won't light up "PostgreSQL").
function buildHighlightRe(terms) {
    const sorted = (terms || [])
        .filter(Boolean)
        .slice()
        .sort((a, b) => b.length - a.length)
        .map(escapeRegExp)
    if (sorted.length === 0) return new RegExp(METRIC_SRC, "g")
    const termSrc = `(?<![A-Za-z0-9])(?:${sorted.join("|")})(?![A-Za-z0-9])`
    return new RegExp(`${METRIC_SRC}|${termSrc}`, "g")
}

// A row's brand color can be quite dark (e.g. Capital One navy). On the black
// page that would be unreadable, so nudge only the too-dark colors toward white
// while keeping their hue.
function ensureReadable(hex) {
    const h = hex.replace("#", "")
    if (h.length !== 6) return hex
    const r = parseInt(h.slice(0, 2), 16)
    const g = parseInt(h.slice(2, 4), 16)
    const b = parseInt(h.slice(4, 6), 16)
    const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b // 0..255
    if (lum >= 90) return hex
    const t = ((90 - lum) / 90) * 0.7 // mix factor toward white
    const mix = (c) => Math.round(c + (255 - c) * t)
    const toHex = (c) => mix(c).toString(16).padStart(2, "0")
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

// Flatten a row's media into (a) strip thumbnails and (b) a flat list of
// full-size images the lightbox pages through. A "deck" entry — a slide deck
// pre-rendered to page images — contributes a single thumbnail but all of its
// pages, and carries a `doc` link to the original file. We render decks as
// images rather than embedding the PDF because browsers render an embedded PDF
// unreliably (it often shows blank in an <iframe>).
function buildGallery(media) {
    const images = [] // flat list of full-size image srcs
    const strip = [] // one entry per media item: { thumb, index, badge?, doc? }
    for (const m of media || []) {
        if (m && m.type === "deck") {
            strip.push({
                thumb: m.thumb || m.pages[0],
                index: images.length,
                badge: `${m.pages.length} slides`,
                doc: m.src,
            })
            images.push(...m.pages)
        } else {
            const src = typeof m === "string" ? m : m.src
            strip.push({ thumb: src, index: images.length })
            images.push(src)
        }
    }
    return { images, strip }
}

// Wrap each metric / tooling token in a colored, softly glowing span so a row's
// achievements and tech pop in its brand color; everything else stays plain.
function highlight(text, color, re) {
    const nodes = []
    let last = 0
    let m
    re.lastIndex = 0
    while ((m = re.exec(text)) !== null) {
        if (m.index > last) nodes.push(text.slice(last, m.index))
        nodes.push(
            <span
                key={m.index}
                style={{ color, textShadow: `0 0 6px ${color}66` }}
            >
                {m[0]}
            </span>
        )
        last = m.index + m[0].length
    }
    if (last < text.length) nodes.push(text.slice(last))
    return nodes
}

// A stacked, borderless accordion row. Clicking the header expands the
// description inline, with the pixel "unloading" dissolve playing over the panel
// as it opens.
//
//  - `company`: when set (jobs), it's rendered as the prominent glowing title and
//    `name` (the role) drops to a secondary line.
//  - `icon` / `iconBg` / `iconColor`: a colored tile with an original pixel-art
//    icon (see PixelIcon) used as the row's icon. `iconOutline` adds an accent
//    ring, for a tile dark enough to otherwise disappear into the page.
//  - `logo`: real company logo revealed on the side when the row is expanded.
//  - `media`: array of image paths shown as a scrollable screenshot gallery.
//  - `align`: "left" (default) or "right" — mirrors the whole row.
//  - `big`: bumps the sizing up a notch (used for jobs).
export default function Project({
    name,
    company,
    tagline,
    description,
    tech,
    icon,
    iconBg,
    iconColor = "#ffffff",
    iconOutline = false,
    logo,
    media,
    github,
    site,
    align = "left",
    big = false,
}) {
    const [open, setOpen] = useState(false)
    const [preview, setPreview] = useState(null) // index of image shown in lightbox
    const right = align === "right"

    // The row's "main color" (its icon tile) drives the skill + metric coloring.
    const mainColor = ensureReadable(iconBg || "#ffffff")
    const glow = `0 0 6px ${mainColor}66`
    const highlightRe = buildHighlightRe(tech)
    const { images, strip } = useMemo(() => buildGallery(media), [media])
    const docs = strip.filter((s) => s.doc)

    // Lightbox keyboard controls: Esc closes, arrows page through the gallery.
    useEffect(() => {
        if (preview === null || images.length === 0) return
        const onKey = (e) => {
            if (e.key === "Escape") setPreview(null)
            else if (e.key === "ArrowRight") setPreview((p) => (p + 1) % images.length)
            else if (e.key === "ArrowLeft") setPreview((p) => (p + images.length - 1) % images.length)
        }
        window.addEventListener("keydown", onKey)
        return () => window.removeEventListener("keydown", onKey)
    }, [preview, images.length])

    const titleSize = big ? "text-3xl md:text-4xl" : "text-2xl md:text-3xl"
    const toggleSize = big ? "text-3xl md:text-4xl" : "text-2xl md:text-3xl"
    const roleSize = big ? "text-lg md:text-xl" : "text-base md:text-lg"
    const taglineSize = big ? "text-base md:text-lg" : "text-sm md:text-base"

    return (
        <div className={`retro-row w-full ${open ? "is-open" : ""}`}>
            <button
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                className={`flex w-full flex-col gap-1 py-4 md:gap-4 md:items-center ${
                    right
                        ? "items-end text-right md:flex-row-reverse"
                        : "items-start text-left md:flex-row"
                }`}
            >
                {icon && (
                    <span
                        className={`job-tile ${iconOutline ? "job-tile--outline" : ""}`}
                        style={{ backgroundColor: iconBg, color: iconColor }}
                    >
                        <PixelIcon name={icon} className="job-icon-art" />
                    </span>
                )}

                <span className={`flex min-w-0 max-w-full flex-col gap-0.5 ${right ? "items-end" : "items-start"}`}>
                    <span className={`retro-title break-words ${titleSize}`}>
                        {company || name}
                    </span>
                    {company && (
                        <span className={`${roleSize} italic opacity-75`}>{name}</span>
                    )}
                </span>

                <span className={`min-w-0 flex-1 break-words italic opacity-60 ${taglineSize}`}>
                    {tagline}
                </span>

                <span className={`header opacity-80 ${toggleSize}`}>
                    {open ? "[-]" : "[+]"}
                </span>
            </button>

            {open && (
                <div
                    className={`relative overflow-hidden pb-8 ${
                        right ? "pr-1 text-right md:pr-4" : "pl-1 md:pl-4"
                    }`}
                >
                    <div className={`flex gap-6 ${logo ? "flex-col md:flex-row md:items-start" : ""}`}>
                        <div className="flex-1">
                            {description.map((item, idx) => (
                                <p key={idx} className="py-2 text-lg md:text-xl">
                                    {highlight(item, mainColor, highlightRe)}
                                </p>
                            ))}

                            {tech && tech.length > 0 && (
                                <p className="mt-5 text-base md:text-lg">
                                    <span className="opacity-70">&gt; stack:</span>{" "}
                                    {tech.map((t, i) => (
                                        <span key={i}>
                                            {i > 0 && (
                                                <span className="opacity-40">{"  ·  "}</span>
                                            )}
                                            <span style={{ color: mainColor, textShadow: glow }}>
                                                {t}
                                            </span>
                                        </span>
                                    ))}
                                </p>
                            )}

                            {(github || site || docs.length > 0) && (
                                <div className={`mt-4 flex flex-row gap-8 ${right ? "justify-end" : ""}`}>
                                    {github && (
                                        <a href={github} className="retro-link text-lg md:text-xl">
                                            &gt; github
                                        </a>
                                    )}
                                    {site && (
                                        <a href={site} className="retro-link text-lg md:text-xl">
                                            &gt; site
                                        </a>
                                    )}
                                    {docs.map((it, i) => (
                                        <a
                                            key={i}
                                            href={it.src}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="retro-link text-lg md:text-xl"
                                        >
                                            &gt; slides
                                        </a>
                                    ))}
                                </div>
                            )}

                            {strip.length > 0 && (
                                <div className="media-strip mt-6">
                                    {strip.map((s, i) => (
                                        <button
                                            key={i}
                                            type="button"
                                            onClick={() => setPreview(s.index)}
                                            className="media-thumb-btn shrink-0 border-0 bg-transparent p-0"
                                            aria-label={`Open ${name} ${s.badge ? "slides" : "screenshot"} ${i + 1}`}
                                        >
                                            <img
                                                src={s.thumb}
                                                alt={`${name} ${s.badge ? "slides" : "screenshot"} ${i + 1}`}
                                                className="media-thumb"
                                                loading="lazy"
                                            />
                                            {s.badge && (
                                                <span className="media-badge header">{s.badge}</span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {logo && (
                            <img
                                src={logo}
                                alt={`${company} logo`}
                                className="job-logo"
                            />
                        )}
                    </div>

                    <PixelReveal trigger={open} />
                </div>
            )}

            {preview !== null && images[preview] && (
                <div
                    className="media-lightbox"
                    onClick={() => setPreview(null)}
                    role="dialog"
                    aria-modal="true"
                >
                    <button
                        type="button"
                        className="media-close header"
                        onClick={() => setPreview(null)}
                        aria-label="Close preview"
                    >
                        [x]
                    </button>

                    {images.length > 1 && (
                        <button
                            type="button"
                            className="media-nav media-prev header"
                            onClick={(e) => {
                                e.stopPropagation()
                                setPreview((p) => (p + images.length - 1) % images.length)
                            }}
                            aria-label="Previous"
                        >
                            {"<"}
                        </button>
                    )}

                    <img
                        src={images[preview]}
                        alt={`${name} preview ${preview + 1}`}
                        className="media-full"
                        onClick={(e) => e.stopPropagation()}
                    />

                    {images.length > 1 && (
                        <button
                            type="button"
                            className="media-nav media-next header"
                            onClick={(e) => {
                                e.stopPropagation()
                                setPreview((p) => (p + 1) % images.length)
                            }}
                            aria-label="Next"
                        >
                            {">"}
                        </button>
                    )}

                    {images.length > 1 && (
                        <span className="media-counter header">
                            {preview + 1} / {images.length}
                        </span>
                    )}
                </div>
            )}
        </div>
    )
}
